import axios from 'axios'
import type {
  User,
  LoginResponse,
  Verify2FAResponse,
  ProfileResponse,
  CatalogProduct,
  CatalogProductListResponse,
  CatalogProductFormData,
  CatalogProductOptions,
  Customer,
  CustomerListResponse,
  CustomerSecurity,
  CustomerFormData,
  Order,
  OrderListResponse,
  OrderFormData,
  OrderStatus,
  OrderMessageListResponse,
  OrderMessageInboxResponse,
  FeaturedProduct,
  FeaturedProductOptions,
  FeaturedProductFormData,
  Manufacturer,
  ManufacturerFormData,
  ManufacturerListResponse,
  PublicManufacturer,
  SiteSettings,
  PublicSiteSettings,
  Category,
  PublicCategory,
  ProductType,
  ProductTypeListResponse,
  ProductLiterature,
  ProductLiteratureFormData,
  ProductLiteratureListResponse,
  Notification,
  NotificationListResponse,
  NotificationUnreadCountResponse,
  InvoiceListResponse,
} from './types'

// Create axios instance with base URL. Use Vite env override or default to relative '/api'
const api = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL as string) || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
})

// Response interceptor to handle auth errors and extract backend error messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login/auth endpoints - these are expected to return 401 on wrong credentials
      // Also don't redirect for profile check (used by AuthContext)
      const url = error.config?.url || ''
      if (url.includes('/user/profile') ||
          url.includes('/auth/login') ||
          url.includes('/auth/verify-2fa')) {
        return Promise.reject(error)
      }

      // For any other 401, the session has expired mid-action
      // Redirect to home with session expired banner
      window.location.href = '/?banner=session-expired'
    }

    // Extract the backend error message so mutation.error.message is human-readable
    const backendMessage = error.response?.data?.error || error.response?.data?.message
    if (backendMessage) {
      error.message = backendMessage
    }

    return Promise.reject(error)
  }
)

// Auth API calls
export const authAPI = {
  login: async (email: string, password: string, turnstileToken: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', {
      email,
      password,
      turnstileToken
    })
    // Token is now in httpOnly cookie, just return user data
    return response.data
  },

  verify2FA: async (email: string, code: string): Promise<Verify2FAResponse> => {
    const response = await api.post('/auth/verify-2fa', { email, code })
    // Token is now in httpOnly cookie after successful 2FA verification
    return response.data
  },

  logout: async () => {
    const response = await api.post('/auth/logout')
    return response.data
  },

  forgotPassword: async (email: string, firstName: string, lastName: string, turnstileToken: string) => {
    const response = await api.post('/auth/forgot-password', {
      email,
      first_name: firstName,
      last_name: lastName,
      turnstileToken
    })
    return response.data
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password })
    return response.data
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    })
    return response.data
  },

  toggle2FA: async (enable: boolean) => {
    const response = await api.post('/auth/toggle-2fa', { enable })
    return response.data
  }
}

// User API calls
export const userAPI = {
  getProfile: async (): Promise<ProfileResponse> => {
    const response = await api.get('/user/profile')
    return response.data
  },

  updateProfile: async (userData: Partial<User>) => {
    const response = await api.put('/user/profile', userData)
    return response.data
  },

  updateNotificationPreferences: async (prefs: Record<string, boolean>) => {
    const response = await api.put('/user/notification-preferences', prefs)
    return response.data
  },
}

// Contact form API
export interface ContactFormData {
  name: string
  email: string
  message: string
  company?: string
  phone?: string
  partInquiry?: string
  turnstileToken: string
}

export const demoAPI = {
  requestAccess: async (data: { email: string; company: string; role: string; turnstileToken: string }) => {
    const response = await api.post('/auth/demo-access', data)
    return response.data as { demo_token: string }
  },
  login: async (data: { demo_token: string; role: string }) => {
    const response = await api.post('/auth/demo-login', data)
    return response.data
  },
}

export const contactAPI = {
  submit: async (formData: ContactFormData) => {
    const response = await api.post('/contact/submit', formData)
    return response.data
  },
  submitSupport: async (data: { category: string; subject: string; message: string }) => {
    const response = await api.post('/contact/support', data)
    return response.data
  }
}

// Admin API calls
export const adminAPI = {
  getUsers: async (page = 1, perPage = 20, search = '', adminsOnly = false) => {
    const response = await api.get('/admin/users', {
      params: { page, per_page: perPage, search, admins_only: adminsOnly }
    })
    return response.data
  },

  getUser: async (userId: number) => {
    const response = await api.get(`/admin/users/${userId}`)
    return response.data
  },

  updateUser: async (userId: number, userData: Partial<User>) => {
    const response = await api.put(`/admin/users/${userId}`, userData)
    return response.data
  },

  createUser: async (userData: Partial<User> & { password?: string }) => {
    const response = await api.post('/admin/users', userData)
    return response.data
  },

  inviteAdmin: async (email: string) => {
    const response = await api.post('/admin/users/invite-admin', { email })
    return response.data
  },

  resendAdminInvite: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/resend-admin-invite`)
    return response.data
  },

  getAdminUser: async (userId: number) => {
    const response = await api.get(`/admin/users/${userId}`)
    return response.data
  },

  deleteAdminUser: async (userId: number) => {
    const response = await api.delete(`/admin/users/${userId}`)
    return response.data
  },

  getStats: async () => {
    const response = await api.get('/admin/stats')
    return response.data
  },

  getAuditLogs: async (page = 1, perPage = 50) => {
    const response = await api.get('/admin/audit-logs', {
      params: { page, per_page: perPage }
    })
    return response.data
  },

  impersonateUser: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/impersonate`)
    return response.data
  },

  exitImpersonation: async () => {
    const response = await api.post('/admin/exit-impersonation')
    return response.data
  }
}

// Product Catalog API calls (admin only)
export const productAPI = {
  getProducts: async (
    page = 1,
    perPage = 20,
    search = '',
    category = '',
    manufacturer = '',
    includeInactive = false,
    inactiveOnly = false,
    productType = ''
  ): Promise<CatalogProductListResponse> => {
    const response = await api.get('/admin/products/', {
      params: {
        page,
        per_page: perPage,
        search,
        category,
        manufacturer,
        include_inactive: includeInactive,
        inactive_only: inactiveOnly,
        product_type: productType,
      }
    })
    return response.data
  },

  getProduct: async (productId: number): Promise<{ product: CatalogProduct }> => {
    const response = await api.get(`/admin/products/${productId}`)
    return response.data
  },

  getRelatedProducts: async (productId: number): Promise<{ products: CatalogProduct[] }> => {
    const response = await api.get(`/admin/products/${productId}/related`)
    return response.data
  },

  getOptions: async (): Promise<CatalogProductOptions> => {
    const response = await api.get('/admin/products/options')
    return response.data
  },

  createProduct: async (productData: CatalogProductFormData): Promise<{ message: string; product: CatalogProduct }> => {
    const response = await api.post('/admin/products/', productData)
    return response.data
  },

  updateProduct: async (productId: number, productData: Partial<CatalogProductFormData & { is_active: boolean }>): Promise<{ message: string; product: CatalogProduct }> => {
    const response = await api.put(`/admin/products/${productId}`, productData)
    return response.data
  },

  deleteProduct: async (productId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/products/${productId}`)
    return response.data
  },

  // Bulk operations
  bulkCreateProducts: async (products: CatalogProductFormData[]): Promise<{
    message: string
    created: number
    errors: Array<{ row: number; error: string }>
  }> => {
    const response = await api.post('/admin/products/bulk', { products })
    return response.data
  },

  bulkUpdateProducts: async (ids: number[], updates: Partial<{ is_active: boolean }>): Promise<{
    message: string
    updated: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.patch('/admin/products/bulk', { ids, updates })
    return response.data
  },

  bulkDeleteProducts: async (ids: number[]): Promise<{
    message: string
    deleted: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.request({
      method: 'DELETE',
      url: '/admin/products/bulk',
      data: { ids }
    })
    return response.data
  },

  checkSkus: async (skus: string[]): Promise<{
    existing: string[]
    new: string[]
  }> => {
    const response = await api.post('/admin/products/check-skus', { skus })
    return response.data
  },

  bulkUpsertProducts: async (products: CatalogProductFormData[]): Promise<{
    message: string
    created: number
    updated: number
    errors: Array<{ row: number; error: string }>
  }> => {
    const response = await api.post('/admin/products/bulk-upsert', { products })
    return response.data
  },

  bulkSetActive: async (ids: number[], isActive: boolean): Promise<{
    message: string
    updated: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.patch('/admin/products/bulk', { ids, updates: { is_active: isActive } })
    return response.data
  },

  bulkSetManufacturer: async (ids: number[], manufacturerId: number): Promise<{
    message: string
    updated: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.patch('/admin/products/bulk', { ids, updates: { manufacturer_id: manufacturerId } })
    return response.data
  },

  bulkSetCategory: async (ids: number[], categoryId: number): Promise<{
    message: string
    updated: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.patch('/admin/products/bulk', { ids, updates: { category_id: categoryId } })
    return response.data
  },

  bulkSetProductType: async (ids: number[], productTypeId: number): Promise<{
    message: string
    updated: number
    errors: Array<{ id: number; error: string }>
  }> => {
    const response = await api.patch('/admin/products/bulk', { ids, updates: { product_type_id: productTypeId } })
    return response.data
  },
}

// Customer API calls (admin only)
export const customerAPI = {
  getCustomers: async (page = 1, perPage = 20, search = '', accountStatus = '', includeInactive = false): Promise<CustomerListResponse> => {
    const params: Record<string, unknown> = { page, per_page: perPage, search, account_status: accountStatus }
    if (includeInactive) params.include_inactive = true
    const response = await api.get('/admin/customers', { params })
    return response.data
  },

  getCustomer: async (customerId: number): Promise<{ customer: Customer; orders: Order[]; security: CustomerSecurity }> => {
    const response = await api.get(`/admin/customers/${customerId}`)
    return response.data
  },

  unlockCustomer: async (customerId: number): Promise<{ message: string; customer: Customer }> => {
    const response = await api.post(`/admin/customers/${customerId}/unlock`)
    return response.data
  },

  resendInvite: async (customerId: number): Promise<{ message: string }> => {
    const response = await api.post(`/admin/customers/${customerId}/resend-invite`)
    return response.data
  },

  createCustomer: async (customerData: CustomerFormData): Promise<{ message: string; customer: Customer }> => {
    const response = await api.post('/admin/customers', customerData)
    return response.data
  },

  updateCustomer: async (customerId: number, customerData: Partial<CustomerFormData & { is_active: boolean }>): Promise<{ message: string; customer: Customer }> => {
    const response = await api.put(`/admin/customers/${customerId}`, customerData)
    return response.data
  },

  // Bulk operations
  bulkCreateCustomers: async (customers: CustomerFormData[]): Promise<{
    message: string
    created: number
    errors: Array<{ row: number; error: string }>
  }> => {
    const response = await api.post('/admin/customers/bulk', { customers })
    return response.data
  },

  bulkSetStatus: async (ids: number[], updates: { is_active?: boolean; account_status?: string }): Promise<{ message: string; updated: number }> => {
    const response = await api.patch('/admin/customers/bulk-status', { ids, ...updates })
    return response.data
  },

  deleteCustomer: async (customerId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/customers/${customerId}`)
    return response.data
  },

  bulkDelete: async (ids: number[]): Promise<{ message: string; deleted: number }> => {
    const response = await api.delete('/admin/customers/bulk', { data: { ids } })
    return response.data
  },
}

// Order API calls (admin only)
export const orderAPI = {
  getOrders: async (page = 1, perPage = 20, search = '', status = '', customerId?: number): Promise<OrderListResponse> => {
    const response = await api.get('/admin/orders/', {
      params: { page, per_page: perPage, search, status, customer_id: customerId }
    })
    return response.data
  },

  getOrder: async (orderId: number): Promise<{ order: Order }> => {
    const response = await api.get(`/admin/orders/${orderId}`)
    return response.data
  },

  createOrder: async (orderData: OrderFormData): Promise<{ message: string; order: Order }> => {
    const response = await api.post('/admin/orders/customer', orderData)
    return response.data
  },

  updateOrder: async (orderId: number, orderData: Partial<OrderFormData>): Promise<{ message: string; order: Order }> => {
    const response = await api.put(`/admin/orders/${orderId}`, orderData)
    return response.data
  },

  updateOrderStatus: async (
    orderId: number,
    status: OrderStatus,
    tracking?: { tracking_number: string; carrier: string }
  ): Promise<{ message: string; order: Order }> => {
    const response = await api.put(`/admin/orders/${orderId}/status`, { status, ...tracking })
    return response.data
  },

  getCustomerOrders: async (customerId: number, page = 1, perPage = 20): Promise<OrderListResponse> => {
    const response = await api.get(`/admin/orders/customer/${customerId}`, {
      params: { page, per_page: perPage }
    })
    return response.data
  },

  // Customer-facing endpoint to get their own orders
  getMyOrders: async (page = 1, perPage = 20, search = ''): Promise<OrderListResponse> => {
    const response = await api.get('/admin/orders/my-orders', {
      params: { page, per_page: perPage, search: search || undefined }
    })
    return response.data
  },

  bulkUpdateStatus: async (ids: number[], status: string): Promise<{ message: string; updated: number }> => {
    const response = await api.patch('/admin/orders/bulk-status', { ids, status })
    return response.data
  },

  // Admin fulfillment confirmation — sets fulfilled quantities, may create backorder
  fulfillOrder: async (
    orderId: number,
    items: Array<{ order_item_id: number; fulfilled_quantity: number }>
  ): Promise<{ message: string; order: Order; backorder?: Order }> => {
    const response = await api.post(`/admin/orders/${orderId}/fulfill`, { items })
    return response.data
  },

  // Cancel a child backorder from its parent order
  cancelBackorder: async (orderId: number, backorderId: number): Promise<{ message: string; order: Order }> => {
    const response = await api.post(`/admin/orders/${orderId}/backorders/${backorderId}/cancel`)
    return response.data
  },

  // Customer-initiated order cancellation (pending orders only)
  cancelOrder: async (orderId: number): Promise<{ message: string; order: Order }> => {
    const response = await api.post(`/admin/orders/customer/${orderId}/cancel`)
    return response.data
  },
}

// Featured Products API calls
export const featuredProductAPI = {
  // Public endpoint to get featured products
  getPublicFeaturedProducts: async (): Promise<{ products: FeaturedProduct[] }> => {
    const response = await api.get('/featured-products/public')
    return response.data
  },

  // Admin endpoints
  getFeaturedProducts: async (): Promise<{ slots: FeaturedProduct[] }> => {
    const response = await api.get('/featured-products/')
    return response.data
  },

  getOptions: async (): Promise<FeaturedProductOptions> => {
    const response = await api.get('/featured-products/options')
    return response.data
  },

  getFeaturedProduct: async (slotNumber: number): Promise<{ slot: FeaturedProduct }> => {
    const response = await api.get(`/featured-products/${slotNumber}`)
    return response.data
  },

  updateFeaturedProduct: async (slotNumber: number, data: Partial<FeaturedProductFormData>): Promise<{ message: string; slot: FeaturedProduct }> => {
    const response = await api.put(`/featured-products/${slotNumber}`, data)
    return response.data
  },

  clearFeaturedProduct: async (slotNumber: number): Promise<{ message: string }> => {
    const response = await api.delete(`/featured-products/${slotNumber}`)
    return response.data
  },

  reorderFeaturedProducts: async (slotNumbers: number[]): Promise<{ message: string }> => {
    const response = await api.post('/featured-products/reorder', { slot_numbers: slotNumbers })
    return response.data
  },
}

// Manufacturer API calls (admin + public)
export const manufacturerAPI = {
  getManufacturers: async (page = 1, perPage = 20, search = ''): Promise<ManufacturerListResponse> => {
    const response = await api.get('/admin/manufacturers/', {
      params: { page, per_page: perPage, search }
    })
    return response.data
  },

  getPublicManufacturers: async (): Promise<{ manufacturers: PublicManufacturer[] }> => {
    const response = await api.get('/admin/manufacturers/public')
    return response.data
  },

  createManufacturer: async (manufacturerData: ManufacturerFormData): Promise<{ message: string; manufacturer: Manufacturer }> => {
    const response = await api.post('/admin/manufacturers/', manufacturerData)
    return response.data
  },

  updateManufacturer: async (manufacturerId: number, manufacturerData: Partial<ManufacturerFormData>): Promise<{ message: string; manufacturer: Manufacturer }> => {
    const response = await api.put(`/admin/manufacturers/${manufacturerId}`, manufacturerData)
    return response.data
  },

  deleteManufacturer: async (manufacturerId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/manufacturers/${manufacturerId}`)
    return response.data
  },
}

// Site Settings API calls (admin + public)
export const settingsAPI = {
  getPublicSettings: async (): Promise<PublicSiteSettings> => {
    const response = await api.get('/admin/settings/public')
    return response.data
  },

  getSettings: async (): Promise<{ settings: SiteSettings }> => {
    const response = await api.get('/admin/settings/')
    return response.data
  },

  updateSettings: async (data: Partial<Omit<SiteSettings, 'id' | 'updated_at' | 'theme'>>): Promise<{ message: string; settings: SiteSettings }> => {
    const response = await api.put('/admin/settings/', data)
    return response.data
  },

  getThemes: async (): Promise<{ themes: import('./types').ThemePalette[] }> => {
    const response = await api.get('/admin/settings/themes')
    return response.data
  },
}

// Upload API calls (admin only)
export const uploadAPI = {
  uploadImage: async (file: File, folder = 'images'): Promise<{ url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('folder', folder)
    const response = await api.post('/admin/uploads/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  uploadPdf: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/admin/uploads/pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

// Product Type API calls (admin only)
export const productTypeAPI = {
  getProductTypes: async (params?: { category?: string; category_id?: number }): Promise<ProductTypeListResponse> => {
    const response = await api.get('/admin/product-types/', { params })
    return response.data
  },

  getPublicProductTypes: async (params?: { category?: string }): Promise<ProductTypeListResponse> => {
    const response = await api.get('/admin/product-types/public', { params })
    return response.data
  },

  createProductType: async (data: { name: string; category?: string; category_id?: number }): Promise<{ message: string; product_type: ProductType }> => {
    const response = await api.post('/admin/product-types/', data)
    return response.data
  },

  updateProductType: async (id: number, data: { name: string }): Promise<{ message: string; product_type: ProductType }> => {
    const response = await api.put(`/admin/product-types/${id}`, data)
    return response.data
  },

  deleteProductType: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/product-types/${id}`)
    return response.data
  },
}

// Category API calls (admin + public)
export const categoryAPI = {
  getCategories: async (params?: { page?: number; perPage?: number; search?: string }): Promise<{ categories: Category[]; total: number; pages: number; current_page: number }> => {
    const response = await api.get('/admin/categories/', {
      params: {
        page: params?.page || 1,
        per_page: params?.perPage || 20,
        search: params?.search || '',
      },
    })
    return response.data
  },

  getPublicCategories: async (): Promise<{ categories: PublicCategory[] }> => {
    const response = await api.get('/admin/categories/public')
    return response.data
  },

  upsertCategory: async (data: { name: string; image_url?: string | null; description?: string }): Promise<{ message: string; category: Category }> => {
    const response = await api.post('/admin/categories/', data)
    return response.data
  },

  updateCategory: async (categoryId: number, data: Partial<{ name: string; image_url: string | null; description: string }>): Promise<{ message: string; category: Category }> => {
    const response = await api.put(`/admin/categories/${categoryId}`, data)
    return response.data
  },

  deleteCategory: async (categoryId: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/categories/${categoryId}`)
    return response.data
  },
}

// Literature API calls (admin + public)
export const literatureAPI = {
  getPublicLiterature: async (filters?: {
    manufacturer_id?: number
    category_id?: number
    product_id?: number
  }): Promise<{ literature: ProductLiterature[] }> => {
    const response = await api.get('/admin/literature/public', { params: filters })
    return response.data
  },

  getLiteratureForProduct: async (productId: number): Promise<{ literature: ProductLiterature[] }> => {
    const response = await api.get(`/admin/literature/for-product/${productId}`)
    return response.data
  },

  getLiterature: async (page = 1, perPage = 20, search = ''): Promise<ProductLiteratureListResponse> => {
    const response = await api.get('/admin/literature/', {
      params: { page, per_page: perPage, search },
    })
    return response.data
  },

  createLiterature: async (data: ProductLiteratureFormData): Promise<{ message: string; literature: ProductLiterature }> => {
    const response = await api.post('/admin/literature/', data)
    return response.data
  },

  updateLiterature: async (id: number, data: Partial<ProductLiteratureFormData>): Promise<{ message: string; literature: ProductLiterature }> => {
    const response = await api.put(`/admin/literature/${id}`, data)
    return response.data
  },

  deleteLiterature: async (id: number): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/literature/${id}`)
    return response.data
  },
}

// Notifications API
export const notificationAPI = {
  getNotifications: async (page = 1, perPage = 20, unreadOnly = false): Promise<NotificationListResponse> => {
    const response = await api.get('/notifications/', { params: { page, per_page: perPage, unread_only: unreadOnly } })
    return response.data
  },

  getUnreadCount: async (): Promise<NotificationUnreadCountResponse> => {
    const response = await api.get('/notifications/unread-count')
    return response.data
  },

  markRead: async (id: number): Promise<{ message: string; notification: Notification }> => {
    const response = await api.put(`/notifications/${id}/read`)
    return response.data
  },

  markAllRead: async (): Promise<{ message: string }> => {
    const response = await api.put('/notifications/read-all')
    return response.data
  },
}

// Order Messages API
export const orderMessageAPI = {
  getMessages: async (orderId: number): Promise<OrderMessageListResponse> => {
    const response = await api.get(`/admin/orders/${orderId}/messages`)
    return response.data
  },

  sendMessage: async (orderId: number, content: string): Promise<{ message: string; data: OrderMessageListResponse['messages'][0] }> => {
    const response = await api.post(`/admin/orders/${orderId}/messages`, { content })
    return response.data
  },

  markRead: async (orderId: number): Promise<{ message: string }> => {
    const response = await api.put(`/admin/orders/${orderId}/messages/read`)
    return response.data
  },

  getUnreadCount: async (): Promise<{ unread_count: number }> => {
    const response = await api.get('/admin/orders/messages/unread-count')
    return response.data
  },

  getInbox: async (): Promise<OrderMessageInboxResponse> => {
    const response = await api.get('/admin/orders/messages/inbox')
    return response.data
  },

  getMyInbox: async (): Promise<OrderMessageInboxResponse> => {
    const response = await api.get('/admin/orders/messages/my-inbox')
    return response.data
  },
}

// Invoice API
export const invoiceAPI = {
  getMyInvoices: async (page = 1, perPage = 20): Promise<InvoiceListResponse> => {
    const response = await api.get('/invoices/my-invoices', { params: { page, per_page: perPage } })
    return response.data
  },

  fetchPdfBlobUrl: async (invoiceId: number): Promise<string> => {
    const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' })
    return URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
  },
}

export default api
